import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type { NotificationsService } from 'tio-ui/components/notifications';
import { trackedFunction } from 'reactiveweb/function';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import { VStack, Section } from 'tio-ui/components/layout';
import {
  PhFloppyDisk,
  PhDownload,
  PhTable,
  PhChartBar,
  PhChartPie,
  PhChartLine,
  PhChartLineUp,
  PhTrash,
  PhCopy,
  PhDownloadSimple,
  PhCaretDown,
  PhCaretUp,
} from 'ember-phosphor-icons';
import SaveReportDialog from './save-report-dialog.gts';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import type IntlService from 'ember-intl/services/intl';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import { ReportLoader, type ReportLoadContext } from '../utils/report-loader';
import { deleteReportRequest } from '../requests/report';
import { SkipCache } from '@warp-drive/core-types/request';
import { query } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';

interface SavedReportsDrawerSignature {
  Args: {
    onLoad: (report: Report) => void;
    onSave?: () => void;
    onExport?: () => void;
    onDelete?: (report: Report) => void;
    searchData?: Record<string, unknown>;
    serializedQuery?: string;
    reportType?: 'chart_builder' | 'table_builder';
    currentReport?: Report;

    // Whether to show partner visibility options (only for global/cross-company context)
    showPartnerVisibility?: boolean;

    // Context functions for ReportLoader
    setDataset?: (dataset: string) => void;
    setGroupByField?: (field: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setChartType?: (type: any) => void;
    setTimeRange?: (range: string) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setDateGrouping?: (grouping: any) => void;
    setShowTableView?: (show: boolean) => void;
    clearQueryCache?: () => void;
    updateQueryParameters?: (params: Record<string, unknown>) => void;
    setCurrentReport?: (report: Report) => void;
  };
}

export default class SavedReportsDrawer extends Component<SavedReportsDrawerSignature> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  @tracked selectedReportId = '';
  @tracked showSaveDialog = false;
  @tracked exportStatus: 'idle' | 'copied' = 'idle';
  @tracked importExpanded = false;
  @tracked importJsonText = '';
  @tracked importStatus: 'idle' | 'importing' | 'success' | 'error' = 'idle';
  @tracked importMessage = '';
  exportTimerId: ReturnType<typeof setTimeout> | null = null;

  reportsRequest = trackedFunction(this, async () => {
    const { content } = (await this.store.request(
      query<Report>(
        'report',
        {
          'page[limit]': 100,
        },
        { namespace: 'api/v2' }
      )
    )) as { content: { data: Report[] } };
    return content.data;
  });

  get defaultReportType(): 'chart_builder' | 'table_builder' {
    return this.args.reportType || 'chart_builder';
  }

  get defaultSearchData() {
    return this.args.searchData || {};
  }

  getReportIcon(report: Report) {
    // Table builder reports get table icons
    if (report.isTableBuilder) {
      return PhTable;
    }

    // For chart builder reports, try to get specific chart type from chartSettings
    const searchConfig = report.searchConfig as {
      chartSettings?: { selectedType?: string };
    };
    const chartType = searchConfig?.chartSettings?.selectedType;

    // If we have a specific chart type, show the appropriate icon
    if (chartType) {
      switch (chartType) {
        case 'bar':
        case 'horizontalBar':
          return PhChartBar;
        case 'pie':
        case 'donut':
          return PhChartPie;
        case 'line':
        case 'area':
          return PhChartLine;
      }
    }

    return PhChartLineUp;
  }

  get availableReports() {
    // Access the reportsRequest to trigger loading
    return this.reportsRequest.value || [];
  }

  get isLoading() {
    return this.reportsRequest.isLoading;
  }

  get hasError() {
    return this.reportsRequest.isError;
  }

  get reportsList() {
    return this.availableReports.map((report) => ({
      id: report.id,
      name: report.displayName,
      type: report.isTableBuilder ? 'Table' : 'Chart',
      visibility: report.visibilityLabel,
      isPrivate: report.isPrivate,
      isPublished: report.isPublished,
      scopeLabel: report.scopeLabel,
      updatedAt: report.updatedAt,
      report: report,
      icon: this.getReportIcon(report),
    }));
  }

  get selectedReport() {
    return this.availableReports.find((r) => r.id === this.selectedReportId);
  }

  get hasReports() {
    return this.reportsList.length > 0;
  }

  selectReport = (reportId: string | null) => {
    this.selectedReportId = reportId ?? '';
    // Auto-load the report when selected (like original dropdown behavior)
    this.loadSelectedReport();
  };

  loadSelectedReport = async () => {
    const report = this.selectedReport;
    if (!report) return;

    const context: ReportLoadContext = {
      setDataset: this.args.setDataset,
      setGroupByField: this.args.setGroupByField,
      setChartType: this.args.setChartType,
      setTimeRange: this.args.setTimeRange,
      setDateGrouping: this.args.setDateGrouping,
      setShowTableView: this.args.setShowTableView,
      clearQueryCache: this.args.clearQueryCache,
      updateQueryParameters: this.args.updateQueryParameters,
      setCurrentReport: this.args.setCurrentReport,
    };

    try {
      const result = await ReportLoader.loadReport(report, context);
      if (!result.success) {
        console.error('Failed to load report:', result.error);
      }
      // Always call onLoad callback to notify parent (for any additional handling)
      this.args.onLoad(report);
    } catch (error) {
      console.error('Error loading report with ReportLoader:', error);
      // Still call onLoad callback as fallback
      this.args.onLoad(report);
    }
  };

  handleSaveReport = () => {
    this.showSaveDialog = true;
  };

  handleCloseSaveDialog = () => {
    this.showSaveDialog = false;
  };

  handleSaveDialogSave = (report: Report) => {
    // Note: Dialog closes automatically via SaveReportDialog.onSubmit -> this.args.onClose()
    this.reportsRequest.retry(); // Refresh the reports list
    this.args.setCurrentReport?.(report); // Set as current so publish buttons appear
    this.args.onSave?.(); // Notify parent if needed
  };

  handleExportCSV = () => {
    this.args.onExport?.();
  };

  // ── Export / Import ─────────────────────────────────────────────

  exportReport = async () => {
    const report = this.selectedReport;
    if (!report) return;

    const payload = {
      name: report.name,
      description: report.description ?? '',
      reportType: report.reportType,
      searchConfig: report.searchConfig,
      visibilityLevel: report.visibilityLevel ?? 'global',
      metadata: report.metadata ?? {},
    };
    const json = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(json);
    } catch {
      // Fallback for non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = json;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    this.exportStatus = 'copied';
    if (this.exportTimerId) clearTimeout(this.exportTimerId);
    this.exportTimerId = setTimeout(() => (this.exportStatus = 'idle'), 2000);
  };

  toggleImport = () => {
    this.importExpanded = !this.importExpanded;
    if (!this.importExpanded) {
      this.importJsonText = '';
      this.importStatus = 'idle';
      this.importMessage = '';
    }
  };

  handleImportInput = (event: Event) => {
    this.importJsonText = (event.target as HTMLTextAreaElement).value;
  };

  importReports = async () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(this.importJsonText);
    } catch {
      this.importStatus = 'error';
      this.importMessage = this.intl.t('analytics.import_invalid_json');
      return;
    }

    // Accept either a bare array or { reports: [...] }
    let reports: Array<Record<string, unknown>>;
    if (Array.isArray(parsed)) {
      reports = parsed;
    } else if (typeof parsed === 'object' && parsed !== null && 'reports' in parsed) {
      reports = (parsed as Record<string, unknown>).reports as Array<Record<string, unknown>>;
    } else {
      this.importStatus = 'error';
      this.importMessage = this.intl.t('analytics.import_expected_format');
      return;
    }

    if (!Array.isArray(reports) || reports.length === 0) {
      this.importStatus = 'error';
      this.importMessage = this.intl.t('analytics.import_expected_format');
      return;
    }

    const invalid: number[] = [];
    for (let i = 0; i < reports.length; i++) {
      const item = reports[i];
      if (typeof item !== 'object' || item === null) {
        invalid.push(i + 1);
        continue;
      }
      if (!item.name || typeof item.name !== 'string' || !item.searchConfig) {
        invalid.push(i + 1);
      }
    }
    if (invalid.length > 0) {
      this.importStatus = 'error';
      this.importMessage = this.intl.t('analytics.import_missing_fields', {
        positions: invalid.join(', '),
      });
      return;
    }

    this.importStatus = 'importing';
    let created = 0;
    let failed = 0;

    const BATCH_SIZE = 5;
    for (let i = 0; i < reports.length; i += BATCH_SIZE) {
      const batch = reports.slice(i, i + BATCH_SIZE);
      const results = await Promise.allSettled(
        batch.map(async (item) => {
          const record = this.store.createRecord('report', {
            name: item.name as string,
            description: (item.description as string) ?? '',
            reportType: (item.reportType as string) ?? 'table_builder',
            searchConfig: (item.searchConfig as Record<string, unknown>) ?? {},
            visibilityLevel: (item.visibilityLevel as string) ?? 'global',
            metadata: (item.metadata as Record<string, unknown>) ?? {},
          }) as Report;
          await saveRecord(this.store, record, { namespace: 'api/v2' });
          return record;
        })
      );

      for (const result of results) {
        if (result.status === 'fulfilled') {
          created++;
        } else {
          failed++;
        }
      }

      this.importMessage = this.intl.t('analytics.import_progress', {
        current: i + batch.length,
        total: reports.length,
      });
    }

    if (failed === 0) {
      this.importStatus = 'success';
      this.importMessage = this.intl.t('analytics.import_success', { count: created });
    } else {
      this.importStatus = 'error';
      this.importMessage = this.intl.t('analytics.import_partial', {
        created,
        failed,
        total: reports.length,
      });
    }

    this.reportsRequest.retry();
  };

  handleDelete = async (report: Report) => {
    if (!report.id) return;

    if (confirm(`Are you sure you want to delete "${report.displayName}"?`)) {
      try {
        const req = deleteReportRequest(report.id);
        req.cacheOptions = { [SkipCache]: true };
        await this.store.request(req);
        this.args.onDelete?.(report);

        // Clear selection if deleted report was selected
        if (this.selectedReportId === report.id) {
          this.selectedReportId = '';
        }

        this.reportsRequest.retry();
      } catch (error) {
        console.error('Failed to delete report:', error);
        this.notifications.add(this.intl.t('analytics.delete_failed'), { appearance: 'error' });
      }
    }
  };

  <template>
    <VStack class="h-full" data-test-saved-reports-drawer>
      <Section>
        <:actions>
          <Button
            @appearance="outlined"
            @intent="success"
            @icon={{PhFloppyDisk}}
            @size="sm"
            {{on "click" this.handleSaveReport}}
            data-test-save-report-button
          >
            Save Report
          </Button>
          <Button
            @appearance="outlined"
            @intent="primary"
            @icon={{PhDownload}}
            @size="sm"
            {{on "click" this.handleExportCSV}}
            data-test-export-csv-button
          >
            Export CSV
          </Button>
        </:actions>
      </Section>

      <div class="tio-row-tight px-4 py-2 border-b tio-border-default">
        <Button
          @appearance="outlined"
          @intent="primary"
          @icon={{PhCopy}}
          @size="sm"
          @isDisabled={{if this.selectedReport false true}}
          {{on "click" this.exportReport}}
          data-test-export-reports-button
        >
          {{if (eq this.exportStatus "copied") "Copied!" "Export JSON"}}
        </Button>
        <Button
          @appearance="outlined"
          @intent={{if this.importExpanded "default" "primary"}}
          @icon={{if this.importExpanded PhCaretUp PhCaretDown}}
          @size="sm"
          {{on "click" this.toggleImport}}
          data-test-import-reports-button
        >
          {{if this.importExpanded "Close" "Import JSON"}}
        </Button>
      </div>

      {{#if this.importExpanded}}
        <Section>
          <:body>
            <VStack @collapsed={{true}}>
              <div class="text-sm tio-fg-muted mb-2">
                {{t "analytics.import_reports_description"}}
              </div>
              <label class="sr-only" for="import-reports-textarea">
                {{t "analytics.import_reports_description"}}
              </label>
              <textarea
                id="import-reports-textarea"
                class="w-full h-32 p-2 text-sm font-mono border tio-border-default rounded tio-bg-surface tio-fg-primary resize-y"
                placeholder="Paste exported report JSON here"
                {{on "input" this.handleImportInput}}
                data-test-import-textarea
              >{{this.importJsonText}}</textarea>
              {{#unless (eq this.importStatus "idle")}}
                <div
                  class="text-sm mt-1
                    {{if
                      (eq this.importStatus 'error')
                      'tio-fg-danger'
                      (if (eq this.importStatus 'success') 'tio-fg-success' 'tio-fg-info')
                    }}"
                  data-test-import-status
                >
                  {{this.importMessage}}
                </div>
              {{/unless}}
              <Button
                @appearance="outlined"
                @intent="success"
                @icon={{PhDownloadSimple}}
                @size="sm"
                @isDisabled={{if (eq this.importStatus "importing") true false}}
                {{on "click" this.importReports}}
                data-test-import-submit-button
              >
                {{if (eq this.importStatus "importing") "Importing..." "Import Reports"}}
              </Button>
            </VStack>
          </:body>
        </Section>
      {{/if}}

      <div class="flex-1">
        {{#if this.isLoading}}
          <Section>
            <:body>
              <div class="text-center tio-fg-muted" data-test-loading-reports>Loading saved
                reports...</div>
            </:body>
          </Section>
        {{else if this.hasError}}
          <Section>
            <:body>
              <div class="text-center tio-fg-danger">Error loading reports. Check console.</div>
            </:body>
          </Section>
        {{else if this.hasReports}}
          <VStack @collapsed={{true}}>
            {{#each this.reportsList as |item|}}
              <Section
                class="transition-colors group
                  {{if
                    (eq this.selectedReportId item.id)
                    'tio-bg-info tio-fg-inverse'
                    'tio-fg-secondary hover:tio-bg-sunken'
                  }}"
              >
                <div class="tio-row-between">
                  <button
                    type="button"
                    class="tio-saved-reports__row-button flex-1 p-3 text-left border-none cursor-pointer"
                    {{on "click" (fn this.selectReport item.id)}}
                    data-test-report-item={{item.id}}
                  >
                    <div class="tio-row">
                      {{#let item.icon as |IconComponent|}}
                        <IconComponent @size="20" class="shrink-0" />
                      {{/let}}
                      <div>
                        <div class="font-medium">{{item.name}}</div>
                        <div class="text-sm opacity-75">
                          {{item.type}}
                          •
                          {{item.visibility}}
                          •
                          <span
                            class="{{if item.isPublished 'tio-fg-info' 'tio-fg-muted'}}"
                          >{{item.scopeLabel}}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                  <Button
                    @size="sm"
                    @intent="danger"
                    @appearance="outlined"
                    @icon={{PhTrash}}
                    {{on "click" (fn this.handleDelete item.report)}}
                    data-test-delete-report={{item.id}}
                    class="flex"
                  />
                </div>
              </Section>
            {{/each}}
          </VStack>

        {{else}}
          <Section>
            <:body>
              <div class="text-center">
                <PhTable @size="48" class="mx-auto tio-fg-muted mb-4" />
                <div class="tio-fg-muted" data-test-no-reports>
                  No saved reports found.
                  <br />
                  Create your first saved report!
                </div>
              </div>
            </:body>
          </Section>
        {{/if}}
      </div>
    </VStack>

    <SaveReportDialog
      @isOpen={{this.showSaveDialog}}
      @onClose={{this.handleCloseSaveDialog}}
      @onSave={{this.handleSaveDialogSave}}
      @searchData={{this.defaultSearchData}}
      @serializedQuery={{@serializedQuery}}
      @reportType={{this.defaultReportType}}
      @currentReport={{@currentReport}}
      @showPartnerVisibility={{@showPartnerVisibility}}
    />
  </template>
}
